import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { storage, STORAGE_KEYS, generateId } from '../utils/storage';
import { useAuth } from './AuthContext';
import { useGymInfra } from './GymInfraContext';
import { useUI } from './UIContext';
import { useMembers } from './MemberContext';
import { supabase } from '../lib/supabase';
import {
  dbAddLockers, dbAddLocker, dbUpdateLocker, dbDeleteLocker,
  dbUpdateGym,
  dbUpdateMember,
  dbUpdateProfileEditRequest,
} from '../lib/db';
import { toCamel } from '../utils/helpers';

const DEFAULT_LOCKER_SETTINGS = { enabled: false, isFree: true, monthlyFee: 0, periods: [1, 3, 6, 12] };

const FacilityContext = createContext(null);

export const FacilityProvider = ({ children }) => {
  const { gym, refreshGym } = useAuth();
  const { gymId, useDB, isDemoMode, isAdminMode, reloadTrigger } = useGymInfra();
  const { showToast } = useUI();
  const { members, patchMemberLocal } = useMembers();

  const [lockers, setLockers] = useState([]);
  const [profileEditRequests, setProfileEditRequests] = useState([]);
  const [gymSettings, setGymSettings] = useState({
    name: 'GYM Manager',
    phone: '',
    address: '',
    businessHours: '06:00 - 22:00',
    logoColor: '#3b82f6',
    lockerSettings: DEFAULT_LOCKER_SETTINGS,
  });
  const [loaded, setLoaded] = useState(false);

  // DB 로드
  useEffect(() => {
    if (useDB && gymId) {
      (async () => {
        try {
          const [{ data: lk }, { data: pe }] = await Promise.all([
            supabase.from('lockers').select('*').eq('gym_id', gymId).order('number', { ascending: true }),
            supabase.from('profile_edit_requests').select('*, members(name, phone)').eq('gym_id', gymId).order('created_at', { ascending: false }),
          ]);
          if (lk) setLockers(toCamel(lk));
          if (pe) {
            setProfileEditRequests(toCamel(pe).map(r => ({
              ...r,
              memberName: r.members?.name,
              memberPhone: r.members?.phone,
            })));
          }
          // gym settings
          let gymData = gym;
          if (isAdminMode) {
            const { data: g } = await supabase.from('gyms').select('*').eq('id', gymId).single();
            gymData = g;
          }
          setGymSettings({
            name: gymData?.name || 'GYM Manager',
            phone: gymData?.phone || '',
            address: gymData?.address || '',
            businessHours: gymData?.business_hours || '06:00 - 22:00',
            logoColor: gymData?.logo_color || '#3b82f6',
            lockerSettings: gymData?.locker_settings ?? DEFAULT_LOCKER_SETTINGS,
          });
        } catch (e) {
          console.error('FacilityContext DB 로드 실패:', e);
        } finally {
          setLoaded(true);
        }
      })();
    } else if (isDemoMode) {
      setLockers(storage.get(STORAGE_KEYS.LOCKERS) || []);
      const saved = storage.get(STORAGE_KEYS.SETTINGS);
      if (saved) setGymSettings(s => ({ ...s, ...saved }));
      setLoaded(true);
    } else {
      setLoaded(true);
    }
  }, [useDB, gymId, isDemoMode, isAdminMode, reloadTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // ================================================================
  // 락커 CRUD
  // ================================================================
  const addLockers = useCallback(async (dataArray) => {
    if (useDB) {
      try {
        const newLockers = await dbAddLockers(gymId, dataArray);
        setLockers(prev => [...prev, ...newLockers].sort((a, b) => a.number - b.number));
        showToast(`락커 ${dataArray.length}개가 추가되었습니다.`);
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newLockers = dataArray.map(data => ({ ...data, id: generateId() }));
      const updated = [...lockers, ...newLockers];
      storage.set(STORAGE_KEYS.LOCKERS, updated);
      setLockers(updated);
      showToast(`락커 ${dataArray.length}개가 추가되었습니다.`);
    }
  }, [useDB, gymId, lockers, showToast]);

  const addLocker = useCallback(async (data) => {
    if (useDB) {
      try {
        const newLocker = await dbAddLocker(gymId, data);
        setLockers(prev => [...prev, newLocker].sort((a, b) => a.number - b.number));
        showToast('락커가 추가되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newLocker = { ...data, id: generateId() };
      const updated = [...lockers, newLocker];
      storage.set(STORAGE_KEYS.LOCKERS, updated);
      setLockers(updated);
      showToast('락커가 추가되었습니다.');
    }
  }, [useDB, gymId, lockers, showToast]);

  const updateLocker = useCallback(async (id, data) => {
    if (useDB) {
      try {
        await dbUpdateLocker(id, data);
        setLockers(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));
        showToast('락커 정보가 수정되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = lockers.map(l => l.id === id ? { ...l, ...data } : l);
      storage.set(STORAGE_KEYS.LOCKERS, updated);
      setLockers(updated);
      showToast('락커 정보가 수정되었습니다.');
    }
  }, [useDB, lockers, showToast]);

  const deleteLocker = useCallback(async (id) => {
    if (useDB) {
      try {
        await dbDeleteLocker(id);
        setLockers(prev => prev.filter(l => l.id !== id));
        showToast('락커가 삭제되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = lockers.filter(l => l.id !== id);
      storage.set(STORAGE_KEYS.LOCKERS, updated);
      setLockers(updated);
      showToast('락커가 삭제되었습니다.', 'error');
    }
  }, [useDB, lockers, showToast]);

  // ================================================================
  // 헬스장 설정
  // ================================================================
  const updateGymSettings = useCallback(async (data) => {
    if (useDB) {
      try {
        await dbUpdateGym(gymId, data);
        setGymSettings(s => ({ ...s, ...data }));
        if (!isAdminMode) await refreshGym();
        showToast('설정이 저장되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = { ...gymSettings, ...data };
      storage.set(STORAGE_KEYS.SETTINGS, updated);
      setGymSettings(updated);
      showToast('설정이 저장되었습니다.');
    }
  }, [useDB, gymId, gymSettings, isAdminMode, refreshGym, showToast]);

  // ================================================================
  // 정보 수정 신청 처리
  // ================================================================
  const approveProfileEdit = useCallback(async (id) => {
    if (!useDB) return;
    try {
      const req = profileEditRequests.find(r => r.id === id);
      if (!req) return;
      const changesApply = {};
      Object.entries(req.changes || {}).forEach(([field, val]) => {
        changesApply[field] = typeof val === 'object' && val !== null && 'after' in val ? val.after : val;
      });
      await dbUpdateMember(req.memberId, changesApply);
      patchMemberLocal(req.memberId, changesApply);
      const patch = { status: 'approved', processedAt: new Date().toISOString() };
      await dbUpdateProfileEditRequest(id, patch);
      setProfileEditRequests(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
      showToast('정보 수정이 승인되었습니다.');
    } catch (e) { showToast(e.message, 'error'); }
  }, [useDB, profileEditRequests, patchMemberLocal, showToast]);

  const rejectProfileEdit = useCallback(async (id, processedNote = '') => {
    if (!useDB) return;
    try {
      const patch = { status: 'rejected', processedAt: new Date().toISOString(), processedNote };
      await dbUpdateProfileEditRequest(id, patch);
      setProfileEditRequests(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
      showToast('정보 수정 신청이 거절되었습니다.', 'error');
    } catch (e) { showToast(e.message, 'error'); }
  }, [useDB, showToast]);

  // ================================================================
  // 백업 / 복원 / CSV (localStorage 전용)
  // ================================================================
  const exportBackup = useCallback(() => {
    const backup = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      data: Object.fromEntries(Object.entries(STORAGE_KEYS).map(([k, v]) => [k, storage.get(v)])),
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gym-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('백업 파일이 다운로드되었습니다.');
  }, [showToast]);

  const { triggerReload } = useGymInfra();

  const importBackup = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const backup = JSON.parse(e.target.result);
        if (!backup.data) throw new Error('Invalid format');
        Object.entries(backup.data).forEach(([k, v]) => {
          if (v !== null && STORAGE_KEYS[k]) storage.set(STORAGE_KEYS[k], v);
        });
        triggerReload();
        showToast('백업 복원이 완료되었습니다.');
      } catch {
        showToast('백업 파일이 올바르지 않습니다.', 'error');
      }
    };
    reader.readAsText(file);
  }, [triggerReload, showToast]);

  const exportMembersCSV = useCallback(() => {
    const headers = ['이름', '성별', '생년월일', '전화번호', '주소', '운동목적', '알게된경로', '상태', '등록일', '메모'];
    const rows = members.map(m => [
      m.name, m.gender === 'male' ? '남' : '여', m.birthDate || m.birth_date, m.phone,
      m.address, m.purpose, m.referral, m.status, m.createdAt || m.created_at, m.memo,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `회원목록-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('회원 목록이 다운로드되었습니다.');
  }, [members, showToast]);

  const resetAllData = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach(k => storage.remove(k));
    localStorage.removeItem('darkMode');
    window.location.reload();
  }, []);

  return (
    <FacilityContext.Provider value={{
      lockers, addLockers, addLocker, updateLocker, deleteLocker,
      gymSettings, updateGymSettings,
      profileEditRequests, approveProfileEdit, rejectProfileEdit,
      exportBackup, importBackup, exportMembersCSV, resetAllData,
      _facilityLoaded: loaded,
    }}>
      {children}
    </FacilityContext.Provider>
  );
};

export const useFacility = () => {
  const ctx = useContext(FacilityContext);
  if (!ctx) throw new Error('useFacility must be used within FacilityProvider');
  return ctx;
};
